import { api } from "./client";
import type {
  CreateInvitationRequest,
  CreateInvitationResponse,
  ListInvitationsParams,
  ListInvitationsResponse,
} from "@mycompbox/shared";

export interface DailyActivityUser {
  userId: string;
  userName: string;
  userEmail: string;
  epicRequestsCreated: number;
  epicsCreated: number;
  featuresCompleted: number;
  tasksCompleted: number;
  epicsCompleted: number;
}

export interface PeriodActivityUser extends DailyActivityUser {
  totalDurationMinutes: number;
  avgDurationPerItem: number;
  aiSessionMinutes: number;
}

export interface KpiMetric {
  current: number;
  previous: number;
  deltaPercent: number | null;
}

export interface KpiSummary {
  activeUsers: KpiMetric;
  totalActions: KpiMetric;
  totalTimeInvestedMinutes: KpiMetric;
  avgActivityPerUser: KpiMetric;
  aiSessionMinutes: KpiMetric;
}

export interface UserReportRow {
  date: string;
  epicRequestsCreated: number;
  epicsCreated: number;
  featuresCompleted: number;
  tasksCompleted: number;
  epicsCompleted: number;
  totalDurationMinutes: number;
  avgDurationPerItem: number;
  aiSessionMinutes: number;
}

export interface UserReportTotals {
  epicRequestsCreated: number;
  epicsCreated: number;
  featuresCompleted: number;
  tasksCompleted: number;
  epicsCompleted: number;
  totalDurationMinutes: number;
  avgDurationPerItem: number;
  aiSessionMinutes: number;
}

export interface UserReport {
  user: { id: string; name: string; email: string };
  rows: UserReportRow[];
  totals: UserReportTotals;
}

// Internal types matching the actual backend response shapes
interface BackendActivityMetrics {
  epicRequestsCreated: number;
  epicsCreated: number;
  featuresCompleted: number;
  tasksCompleted: number;
  epicsCompleted: number;
}

interface BackendTimeMetrics {
  totalDurationMinutes: number;
  avgDurationPerItem: number;
  aiSessionMinutes: number;
  itemsWithDuration: number;
}

interface BackendDailyRow {
  userId: string;
  name: string;
  email: string;
  metrics: BackendActivityMetrics;
}

interface BackendPeriodRow {
  userId: string;
  name: string;
  email: string;
  metrics: BackendActivityMetrics;
  timeMetrics: BackendTimeMetrics;
}

// Internal types matching the actual backend response shapes
interface BackendKpiMetric {
  value: number;
  previousValue: number;
  delta: number | null;
}

interface BackendKpiSummary {
  period: { startDate: string; endDate: string };
  previousPeriod: { startDate: string; endDate: string };
  totalActiveUsers: BackendKpiMetric;
  totalActions: BackendKpiMetric;
  totalTimeInvestedMinutes: BackendKpiMetric;
  avgActivityPerUser: BackendKpiMetric;
  aiSessionMinutes: BackendKpiMetric;
}

interface BackendUserReportTimeMetrics {
  totalDurationMinutes: number;
  avgDurationPerItem: number;
  aiSessionMinutes: number;
}

interface BackendUserReportTotals extends BackendActivityMetrics {
  totalDurationMinutes: number;
  avgDurationPerItem: number;
  aiSessionMinutes: number;
}

interface BackendUserReport {
  userId: string;
  name: string;
  email: string;
  days: Array<{ date: string; metrics: BackendActivityMetrics; timeMetrics?: BackendUserReportTimeMetrics }>;
  totals: BackendUserReportTotals;
}

interface ListUsersParams {
  limit?: number;
  cursor?: string;
}

interface UserResponse {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  isActive: boolean;
  isGlobalAdmin: boolean;
  canDelete: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ListUsersResponse {
  users: UserResponse[];
  meta: {
    hasMore: boolean;
    cursor: string | null;
  };
}

export const adminApi = {
  // Invitations
  // The route replies with `{ data: invitation }`, which is exactly what
  // CreateInvitationResponse models — callers read `response.data`, no cast needed.
  createInvitation: (
    data: CreateInvitationRequest
  ): Promise<CreateInvitationResponse> =>
    api.post<CreateInvitationResponse>("/admin/invitations", data),

  listInvitations: (params?: ListInvitationsParams) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set("status", params.status);
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    if (params?.cursor) searchParams.set("cursor", params.cursor);
    const query = searchParams.toString();
    return api.get<ListInvitationsResponse>(
      `/admin/invitations${query ? `?${query}` : ""}`
    );
  },

  revokeInvitation: (id: string) => api.delete(`/admin/invitations/${id}`),

  // Users
  listUsers: (params?: ListUsersParams) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    if (params?.cursor) searchParams.set("cursor", params.cursor);
    const query = searchParams.toString();
    return api.get<ListUsersResponse>(`/admin/users${query ? `?${query}` : ""}`);
  },

  deactivateUser: (id: string) =>
    api.patch<UserResponse>(`/admin/users/${id}`, { isActive: false }),

  reactivateUser: (id: string) =>
    api.patch<UserResponse>(`/admin/users/${id}`, { isActive: true }),

  updateUserAdmin: (id: string, data: { isGlobalAdmin?: boolean; isActive?: boolean }) =>
    api.patch<UserResponse>(`/admin/users/${id}`, data),

  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),

  // Activity
  getDailyActivity: async (date: string, timezone?: string, userIds?: string[]): Promise<DailyActivityUser[]> => {
    const searchParams = new URLSearchParams({ date });
    if (timezone) searchParams.set("timezone", timezone);
    if (userIds?.length) searchParams.set("userIds", userIds.join(","));
    const response = await api.get<{ data: BackendDailyRow[] }>(
      `/admin/activity/daily?${searchParams.toString()}`
    );
    return response.data.map((row) => ({
      userId: row.userId,
      userName: row.name,
      userEmail: row.email,
      epicRequestsCreated: row.metrics.epicRequestsCreated,
      epicsCreated: row.metrics.epicsCreated,
      featuresCompleted: row.metrics.featuresCompleted,
      tasksCompleted: row.metrics.tasksCompleted,
      epicsCompleted: row.metrics.epicsCompleted,
    }));
  },

  getPeriodActivity: async (
    startDate: string,
    endDate: string,
    timezone?: string,
    userIds?: string[]
  ): Promise<PeriodActivityUser[]> => {
    const searchParams = new URLSearchParams({ startDate, endDate });
    if (timezone) searchParams.set("timezone", timezone);
    if (userIds?.length) searchParams.set("userIds", userIds.join(","));
    const response = await api.get<{ data: BackendPeriodRow[] }>(
      `/admin/activity/period-activity?${searchParams.toString()}`
    );
    return response.data.map((row) => ({
      userId: row.userId,
      userName: row.name,
      userEmail: row.email,
      epicRequestsCreated: row.metrics.epicRequestsCreated,
      epicsCreated: row.metrics.epicsCreated,
      featuresCompleted: row.metrics.featuresCompleted,
      tasksCompleted: row.metrics.tasksCompleted,
      epicsCompleted: row.metrics.epicsCompleted,
      totalDurationMinutes: row.timeMetrics.totalDurationMinutes,
      avgDurationPerItem: row.timeMetrics.avgDurationPerItem,
      aiSessionMinutes: row.timeMetrics.aiSessionMinutes,
    }));
  },

  getKpiSummary: async (
    startDate: string,
    endDate: string,
    timezone?: string,
    userIds?: string[]
  ): Promise<KpiSummary> => {
    const searchParams = new URLSearchParams({ startDate, endDate });
    if (timezone) searchParams.set("timezone", timezone);
    if (userIds?.length) searchParams.set("userIds", userIds.join(","));
    const response = await api.get<{ data: BackendKpiSummary }>(
      `/admin/activity/kpi-summary?${searchParams.toString()}`
    );
    const d = response.data;

    function mapMetric(m: BackendKpiMetric): KpiMetric {
      return { current: m.value, previous: m.previousValue, deltaPercent: m.delta };
    }

    return {
      activeUsers: mapMetric(d.totalActiveUsers),
      totalActions: mapMetric(d.totalActions),
      totalTimeInvestedMinutes: mapMetric(d.totalTimeInvestedMinutes),
      avgActivityPerUser: mapMetric(d.avgActivityPerUser),
      aiSessionMinutes: mapMetric(d.aiSessionMinutes),
    };
  },

  getUserReport: async (
    userId: string,
    startDate: string,
    endDate: string,
    timezone?: string,
    granularity?: "day" | "week" | "month"
  ): Promise<UserReport> => {
    const searchParams = new URLSearchParams({ userId, startDate, endDate });
    if (timezone) searchParams.set("timezone", timezone);
    if (granularity) searchParams.set("granularity", granularity);
    const response = await api.get<{ data: BackendUserReport }>(
      `/admin/activity/user-report?${searchParams.toString()}`
    );
    const report = response.data;
    return {
      user: { id: report.userId, name: report.name, email: report.email },
      rows: report.days.map((day) => ({
        date: day.date,
        epicRequestsCreated: day.metrics.epicRequestsCreated,
        epicsCreated: day.metrics.epicsCreated,
        featuresCompleted: day.metrics.featuresCompleted,
        tasksCompleted: day.metrics.tasksCompleted,
        epicsCompleted: day.metrics.epicsCompleted,
        totalDurationMinutes: day.timeMetrics?.totalDurationMinutes ?? 0,
        avgDurationPerItem: day.timeMetrics?.avgDurationPerItem ?? 0,
        aiSessionMinutes: day.timeMetrics?.aiSessionMinutes ?? 0,
      })),
      totals: {
        epicRequestsCreated: report.totals.epicRequestsCreated,
        epicsCreated: report.totals.epicsCreated,
        featuresCompleted: report.totals.featuresCompleted,
        tasksCompleted: report.totals.tasksCompleted,
        epicsCompleted: report.totals.epicsCompleted,
        totalDurationMinutes: report.totals.totalDurationMinutes ?? 0,
        avgDurationPerItem: report.totals.avgDurationPerItem ?? 0,
        aiSessionMinutes: report.totals.aiSessionMinutes ?? 0,
      },
    };
  },
};
